import React, { FC, useMemo, Fragment, useState } from 'react';
import { DetailSubContainerStyled } from '../styled';
import { Box, Text, Icon, IconSize } from '@bechtel/gds';
import { CrewTimesheetSummaryProps, CrewEmployeeTotalByHourCode } from './types';
import { forEach, map, filter, sumBy, concat, findIndex } from 'lodash';
import { Trans } from '@lingui/macro';
import { CrewEmployeeItem } from './crew-employee-item';
import {
  BoxCellStyled,
  HeaderBoxStyled,
  CrewListBoxStyled,
  CommentsBoxStyled,
  TotalColumnBoxStyled,
  TotalValueBoxStyled,
  TotalValueContainerStyled,
} from './styled';
import { ViewCommentsSidesheet } from '~/components/organisms';
import { TimesheetCrewSummary, TimesheetFormikState } from '../types';
import { useFormikContext } from 'formik';
import { useTimesheet } from '~/detail/context';
import { TimeEntryInput } from '@bechtel/types';

export const CrewTimesheetSummary: FC<CrewTimesheetSummaryProps> = ({
  crewTimesheetRef,
  onCrewTimesheetScroll,
  crew,
}) => {
  const [showViewCommentSidesheet, setShowViewCommentSidesheet] = useState(false);
  const [selectedCrewSummary, setSelectedCrewSummary] = useState<TimesheetCrewSummary | null>(null);
  const { values, setFieldValue } = useFormikContext<TimesheetFormikState>();
  const { crewEmployees, hourTypes } = useTimesheet();

  const timesheetCrewSummaryList = useMemo(() => {
    let crewSummaryList: TimesheetCrewSummary[] = [];
    forEach(crewEmployees, (crewEmployee) => {
      let timeEntries: TimeEntryInput[] = [];
      forEach(values?.timesheetCostCodes, (costCode) => {
        forEach(costCode.hourCodeTimeEntry, (hourCodeTime) => {
          timeEntries = concat(
            timeEntries,
            filter(hourCodeTime.timeEntries, (timeEntry) => timeEntry.crewMemberId === crewEmployee.id)
          );
        });
      });
      crewSummaryList = concat(crewSummaryList, { crewEmployee: crewEmployee, timeEntries: timeEntries });
    });
    return crewSummaryList;
  }, [values]);

  //Get HourType Totals for each crew Employee
  const hourTypeSummaryList: CrewEmployeeTotalByHourCode[] = useMemo(() => {
    let crewEmployeeTotalByHourCodeList: CrewEmployeeTotalByHourCode[] = [];
    forEach(hourTypes, (hourType, index) => {
      const crewEmployeeTotalByHourCode: CrewEmployeeTotalByHourCode = { hourCode: hourType.hourCode, totalHrs: [] };
      forEach(timesheetCrewSummaryList, (timesheetCrewSummary) => {
        const hourTypeTimeEntries = filter(
          timesheetCrewSummary.timeEntries,
          (timeEntry) => timeEntry.hourCode == hourType.hourCode
        );
        const totalByHourType = sumBy(hourTypeTimeEntries, (hourTypeEntry) =>
          hourTypeEntry.hours ? hourTypeEntry.hours : 0
        );
        crewEmployeeTotalByHourCode.totalHrs = concat(crewEmployeeTotalByHourCode.totalHrs, totalByHourType);
      });
      crewEmployeeTotalByHourCodeList = concat(crewEmployeeTotalByHourCodeList, crewEmployeeTotalByHourCode);
    });
    return crewEmployeeTotalByHourCodeList;
  }, [timesheetCrewSummaryList]);

  const totalHoursList: number[] = useMemo(() => {
    let totalHours: number[] = [];
    forEach(timesheetCrewSummaryList, (timesheetCrewSummary) => {
      const hourTypeTimeEntries = filter(
        timesheetCrewSummary.timeEntries,
        (timeEntry) => timeEntry.crewMemberId == timesheetCrewSummary.crewEmployee.id
      );
      const totalByEmployee = sumBy(hourTypeTimeEntries, (hourTypeEntry) =>
        hourTypeEntry.hours ? hourTypeEntry.hours : 0
      );
      totalHours = concat(totalHours, totalByEmployee);
    });
    return totalHours;
  }, [hourTypeSummaryList]);

  const closeSidesheet = () => {
    setShowViewCommentSidesheet(false);
  };

  const onCommentClick = (timesheetCrewSummary) => {
    setShowViewCommentSidesheet(true);
    setSelectedCrewSummary(timesheetCrewSummary);
  };

  const handleCommentsSubmit = (timesheetEntries: TimeEntryInput[]) => {
    if (!timesheetEntries || timesheetEntries.length === 0) return;
    const crewEmployeeIndex = findIndex(
      crewEmployees,
      (crewEmployee) => crewEmployee.id == timesheetEntries[0].crewMemberId
    );
    forEach(timesheetEntries, (timesheetEntry) => {
      const costCodeIndex = findIndex(
        values.timesheetCostCodes,
        (tsCostCode) => tsCostCode.costCode.id === timesheetEntry.costCodeId
      );
      const hourEntryIndex = findIndex(
        values.timesheetCostCodes[costCodeIndex].hourCodeTimeEntry,
        (hourCodeTimeEntry) => hourCodeTimeEntry.hourCode === timesheetEntry.hourCode
      );
      setFieldValue(
        `timesheetCostCodes.${costCodeIndex}.hourCodeTimeEntry.${hourEntryIndex}.timeEntries.${crewEmployeeIndex}.commentId`,
        timesheetEntry.commentId
      );
      setFieldValue(
        `timesheetCostCodes.${costCodeIndex}.hourCodeTimeEntry.${hourEntryIndex}.timeEntries.${crewEmployeeIndex}.otherCommentDescription`,
        timesheetEntry.otherCommentDescription ? timesheetEntry.otherCommentDescription : ''
      );
    });
  };

  return (
    <>
      <DetailSubContainerStyled
        ref={crewTimesheetRef}
        style={{ overflowX: 'auto', overflowY: 'hidden' }}
        onScroll={(e) => {
          onCrewTimesheetScroll(e);
        }}
      >
        <Box flexDirection={{ sm: 'column' }}>
          <Box flexGrow={{ sm: 1 }}>
            <CrewListBoxStyled>
              {map(timesheetCrewSummaryList, (timesheetCrewSummary) => {
                const commentsCount = filter(
                  timesheetCrewSummary.timeEntries,
                  (timeEntry) => !!timeEntry.commentId
                ).length;

                return (
                  <BoxCellStyled key={timesheetCrewSummary.crewEmployee.id} data-testid="crew-summary-item">
                    <CrewEmployeeItem crewMember={timesheetCrewSummary.crewEmployee} crewNumber={crew.crewNumber} />
                    <Box flexGrow={{ sm: 1 }}>
                      <CommentsBoxStyled
                        disabled={commentsCount === 0}
                        onClick={() => onCommentClick(timesheetCrewSummary)}
                        data-testid="view-comment-action"
                      >
                        <Icon
                          iconStyle="solid"
                          fillColor={commentsCount > 0 ? 'green.500' : 'grey.500'}
                          size={IconSize.MEDIUM}
                          icon="comment-alt"
                        ></Icon>
                        <Text size="200" color="grey.1000" data-testid="comments-count">
                          {commentsCount && (commentsCount < 10 ? '0' + commentsCount : commentsCount)}
                        </Text>
                      </CommentsBoxStyled>
                      <Box flexDirection={{ sm: 'column' }}>
                        <Box flexGrow={{ sm: 1 }} alignItems={{ sm: 'center' }}>
                          <Text size="400" color="grey.1000">
                            <Trans>Time In:&nbsp;</Trans>
                          </Text>
                          <Text size="300" color="grey.500">
                            &nbsp;N/A
                          </Text>
                        </Box>
                        <Box flexGrow={{ sm: 1 }} alignItems={{ sm: 'center' }}>
                          <Text size="400" color="grey.1000">
                            <Trans>Time Out:&nbsp;</Trans>
                          </Text>
                          <Text size="300" color="grey.500">
                            &nbsp;N/A
                          </Text>
                        </Box>
                      </Box>
                    </Box>
                  </BoxCellStyled>
                );
              })}
            </CrewListBoxStyled>
            <HeaderBoxStyled>
              <Text
                family="graphik"
                color="grey.600"
                weight="600"
                size="400"
                style={{ lineHeight: 2, opacity: 0.9, whiteSpace: 'nowrap' }}
              >
                <Trans>YOUR CREW&apos;s</Trans>
              </Text>
              <Text family="graphik" color="grey.600" weight="600" size="400" style={{ opacity: 0.9 }}>
                <Trans>TIMESHEET</Trans>
              </Text>
            </HeaderBoxStyled>
          </Box>
          <TotalValueContainerStyled>
            {map(hourTypeSummaryList, (hourTypeSummary) => {
              return (
                <Fragment key={hourTypeSummary.hourCode}>
                  {map(hourTypeSummary.totalHrs, (total, index) => (
                    <TotalValueBoxStyled key={index}>
                      <Text color="grey.600" size="400">
                        {total ? total.toFixed(1) : 0}
                      </Text>
                    </TotalValueBoxStyled>
                  ))}
                  <TotalColumnBoxStyled css={{ position: 'absolute', left: '8px', zIndex: 'initial' }}>
                    <Box my={{ sm: '400' }}>
                      <Text color="grey.600" size="400">
                        {hourTypeSummary.hourCode}&nbsp;<Trans>Total</Trans>
                      </Text>
                    </Box>
                  </TotalColumnBoxStyled>
                </Fragment>
              );
            })}
          </TotalValueContainerStyled>
          <TotalValueContainerStyled>
            {map(totalHoursList, (totalHrs, index) => {
              return (
                <TotalValueBoxStyled key={`total-${index}`}>
                  <Text key={index} color="grey.600" size="400">
                    {totalHrs ? totalHrs.toFixed(1) : 0}
                  </Text>
                </TotalValueBoxStyled>
              );
            })}
            <TotalColumnBoxStyled css={{ position: 'absolute', left: '8px', zIndex: 'initial' }}>
              <Box my={{ sm: '400' }}>
                <Text weight="600" color="grey.1000" size="400">
                  <Trans>Total</Trans>
                </Text>
              </Box>
            </TotalColumnBoxStyled>
          </TotalValueContainerStyled>
        </Box>
      </DetailSubContainerStyled>
      {showViewCommentSidesheet && selectedCrewSummary && (
        <ViewCommentsSidesheet
          crewNumber={crew.crewNumber}
          timesheetCrewSummary={selectedCrewSummary}
          closeSidesheet={closeSidesheet}
          onSubmit={handleCommentsSubmit}
        />
      )}
    </>
  );
};
